import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useIncentiveOfferOperationDetail } from './context/IncentiveOfferOperationDetailContext';
import getOfferReference from '../../utils/getOfferReference';

import IncentiveOfferOperationDetailHeader from './components/IncentiveOfferOperationDetailHeader';
import IncentiveOfferOperationDetailStatusBanner from './components/IncentiveOfferOperationDetailStatusBanner';
import IncentiveOfferOperationDetailSummaryCard from './components/IncentiveOfferOperationDetailSummaryCard';
import IncentiveOfferOperationDetailTimeline from './components/IncentiveOfferOperationDetailTimeline';
import IncentiveOfferOperationDetailPartiesCard from './components/IncentiveOfferOperationDetailPartiesCard';
import IncentiveOfferOperationDetailPaymentCard from './components/IncentiveOfferOperationDetailPaymentCard';
import IncentiveOfferOperationDetailDocumentation from './components/IncentiveOfferOperationDetailDocumentation';
import IncentiveOfferOperationDetailComments from './components/IncentiveOfferOperationDetailComments';
import IncentiveOfferOperationDetailActivityHistory from './components/IncentiveOfferOperationDetailActivityHistory';

const formatDate = (
  date: Date | string | undefined,
  pattern = 'dd MMM yyyy'
) => {
  if (!date) return '-';
  return format(new Date(date), pattern, { locale: es });
};

const IncentiveOfferOperationDetail: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useIncentiveOfferOperationDetail();

  const formattedRequestDate = useMemo(
    () => formatDate(data.operation?.requestDate),
    [data.operation]
  );

  const sellerName = useMemo(() => {
    if (data.incentiveOffer?.isSellerNameHidden) return t('HiddenName');
    if (!data.operation?.seller) return '';
    if (typeof data.operation.seller === 'string') return '';
    return data.operation.seller.name || '';
  }, [data.operation, data.incentiveOffer?.isSellerNameHidden, t]);

  const unitClassName = useMemo(() => {
    // Prefer operation.unitClass (populated in API) over incentiveOffer.unitClass
    const unitClass =
      data.operation?.unitClass ?? data.incentiveOffer?.unitClass;
    if (!unitClass || typeof unitClass === 'string') return '';
    return (unitClass as { name?: string })?.name || '';
  }, [data.operation, data.incentiveOffer]);

  const offerReference = useMemo(
    () => getOfferReference(data.incentiveOffer),
    [data.incentiveOffer]
  );

  const buyerDocumentationEntries = useMemo(
    () =>
      (data.operation?.buyerDocuments || []).map((document) => ({
        document,
        isFromOffer: false as const,
      })),
    [data.operation?.buyerDocuments]
  );

  const sellerDocumentationEntries = useMemo(() => {
    const offerDocs = (data.incentiveOffer?.documents || []).map((doc) => ({
      document: doc,
      isFromOffer: true as const,
    }));
    const opSellerDocs = (data.operation?.sellerDocuments || []).map(
      (document) => ({
        document,
        isFromOffer: false as const,
      })
    );
    return [...offerDocs, ...opSellerDocs];
  }, [data.incentiveOffer?.documents, data.operation?.sellerDocuments]);

  if (!data.operation && !data.isLoading) {
    return (
      <Page id="trading-offer-operation-detail-page">
        <div className="nk-block text-center py-5">
          <em className="icon ni ni-alert-circle fs-1 text-warning mb-3 d-block" />
          <h5>{t('OperationNotFound')}</h5>
          <p className="text-soft">{t('OperationNotFoundDescription')}</p>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={actions.handleGoBack}
          >
            {t('Back')}
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page id="trading-offer-operation-detail-page">
      <IncentiveOfferOperationDetailHeader
        incentiveOffer={data.incentiveOffer}
        operationReference={data.operationReference}
        operationStatus={data.operationStatus}
        formattedRequestDate={formattedRequestDate}
        sellerName={sellerName}
        isAdmin={data.isAdmin}
        societyId={data.societyId}
        societyName={data.societyName}
        onUpdateStatus={actions.handleUpdateStatus}
      />

      <IncentiveOfferOperationDetailStatusBanner
        operationStatus={data.operationStatus}
        unitsCount={data.operation?.unitsCount || 0}
        unitClassName={unitClassName}
      />

      <div className="nk-block">
        <div className="row g-gs">
          {/* Left Column */}
          <div className="col-lg-7">
            <IncentiveOfferOperationDetailSummaryCard
              operation={data.operation}
              incentiveOffer={data.incentiveOffer}
              operationReference={data.operationReference}
              offerReference={offerReference}
              unitClassName={unitClassName}
              formattedRequestDate={formattedRequestDate}
              onGoToOffer={actions.handleGoToOffer}
            />

            <IncentiveOfferOperationDetailTimeline
              steps={data.timelineSteps}
            />
          </div>

          {/* Right Column */}
          <div className="col-lg-5">
            <IncentiveOfferOperationDetailPartiesCard
              buyerInfo={data.buyerInfo}
              sellerInfo={data.sellerInfo}
            />

            <IncentiveOfferOperationDetailPaymentCard />

            <IncentiveOfferOperationDetailDocumentation
              buyerDocuments={buyerDocumentationEntries}
              sellerDocuments={sellerDocumentationEntries}
              sellerName={sellerName}
              incentivesMarketPurchaseContract={
                data.operation?.incentivesMarketPurchaseContract
              }
            />
          </div>
        </div>
      </div>

      <IncentiveOfferOperationDetailComments />

      <IncentiveOfferOperationDetailActivityHistory
        statusHistory={data.operation?.statusHistory || []}
      />

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default IncentiveOfferOperationDetail;
